// SiteHive — shared hooks
import { useEffect, useRef, useState } from 'react';

export function useInView(options = { threshold: 0.2, once: true }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setInView(true);
            if (options.once) obs.unobserve(entry.target);
          } else if (!options.once) {
            setInView(false);
          }
        });
      },
      { threshold: options.threshold || 0.2, rootMargin: options.rootMargin || '0px' }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [options.threshold, options.once, options.rootMargin]);
  return [ref, inView];
}

export function useCountUp(target, { duration = 1400, start = inView => inView } = {}) {
  const [value, setValue] = useState(0);
  const [ref, inView] = useInView({ threshold: 0.4, once: true });
  useEffect(() => {
    if (!inView) return;
    if (typeof target !== 'number') return;
    let raf;
    const startTime = performance.now();
    const animate = (t) => {
      const elapsed = t - startTime;
      const p = Math.min(1, elapsed / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(animate);
    };
    raf = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(raf);
  }, [inView, target, duration]);
  return [ref, value, inView];
}

// Scrambles characters of `text` for `duration` ms when triggered, then snaps to original.
export function useScramble(text, { duration = 450, trigger = true } = {}) {
  const [out, setOut] = useState(text);
  useEffect(() => {
    if (!trigger) return;
    const chars = '!<>-_\\/[]{}—=+*^?#$%&abcdefghijklmnopqrstuvwxyz0123456789';
    const start = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - start) / duration);
      const len = text.length;
      const reveal = Math.floor(p * len);
      let next = '';
      for (let i = 0; i < len; i++) {
        const c = text[i];
        if (i < reveal || c === ' ' || c === '\n') next += c;
        else next += chars[Math.floor(Math.random() * chars.length)];
      }
      setOut(next);
      if (p < 1) raf = requestAnimationFrame(step);
      else setOut(text);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [text, trigger, duration]);
  return out;
}

// Typewriter cycling through phrases
export function useTypewriter(phrases, { typing = 70, deleting = 35, pause = 1600 } = {}) {
  const [index, setIndex] = useState(0);
  const [sub, setSub] = useState('');
  const [phase, setPhase] = useState('typing'); // typing | pausing | deleting
  useEffect(() => {
    const current = phrases[index % phrases.length];
    let timeout;
    if (phase === 'typing') {
      if (sub.length < current.length) {
        timeout = setTimeout(() => setSub(current.slice(0, sub.length + 1)), typing);
      } else {
        timeout = setTimeout(() => setPhase('deleting'), pause);
      }
    } else if (phase === 'deleting') {
      if (sub.length > 0) {
        timeout = setTimeout(() => setSub(current.slice(0, sub.length - 1)), deleting);
      } else {
        setIndex((i) => (i + 1) % phrases.length);
        setPhase('typing');
      }
    }
    return () => clearTimeout(timeout);
  }, [sub, phase, index, phrases, typing, deleting, pause]);
  return sub;
}

export function prefersReducedMotion() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}
